/**
 * Central, dependency-free date/time formatting helpers.
 *
 * Sprint: TREATX-FASTFIX-GLOBAL-DATE-FORMAT-AUDIT-01
 *
 * Rule: All user-facing date/time strings MUST be rendered through the
 * browser/user locale (pass `undefined` as the first argument to native
 * Intl APIs) unless there is a documented technical reason to pin a
 * specific locale.
 *
 * Do NOT pass `'en-US'` to these helpers — that is exactly the drift
 * this utility exists to eliminate.
 *
 * TREATX-BATCH-1.2 D1 (2026-02):
 *   Additionally exports `formatDateDDMMYYYY` and `parseDateDDMMYYYY`
 *   for the Owner-approved user-facing DD/MM/YYYY presentation
 *   standard. The wire/DB format remains canonical ISO
 *   (YYYY-MM-DD); these helpers only touch presentation.
 *
 * Call sites that need bespoke option objects may either:
 *   1. Use these helpers, or
 *   2. Call `toLocaleDateString(undefined, options)` directly.
 */

const DEFAULT_DATE_OPTIONS = {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
};

// OFFERS-UX / GLOBAL-DATE-CLOSURE — resolve the active app locale from the
// i18n singleton when a caller does not pass one explicitly. This lets shared
// presentation helpers honor the Owner locale standard without every call site
// having to thread `i18n.language`. Presentation only; no i18n mutation.
let _i18nRef = null;
try {
  // eslint-disable-next-line global-require
  _i18nRef = require('../i18n/i18n').default;
} catch (e) {
  _i18nRef = null;
}
function _activeLocale(locale) {
  if (locale) return locale;
  try { return (_i18nRef && _i18nRef.language) || 'en'; } catch (e) { return 'en'; }
}

const DEFAULT_TIME_OPTIONS = {
  hour: '2-digit',
  minute: '2-digit',
};

const DEFAULT_DATETIME_OPTIONS = {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
};

function toDate(value) {
  if (value == null || value === '') return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Locale-aware date formatter (no time).
 * Uses the browser/user locale by default.
 */
export function formatDate(value, options = DEFAULT_DATE_OPTIONS) {
  const d = toDate(value);
  if (!d) return '';
  return d.toLocaleDateString(undefined, options);
}

/**
 * Locale-aware time formatter (no date).
 * Uses the browser/user locale by default.
 */
export function formatTime(value, options = DEFAULT_TIME_OPTIONS) {
  const d = toDate(value);
  if (!d) return '';
  return d.toLocaleTimeString(undefined, options);
}

/**
 * Locale-aware combined date + time formatter.
 * Uses the browser/user locale by default.
 */
export function formatDateTime(value, options = DEFAULT_DATETIME_OPTIONS) {
  const d = toDate(value);
  if (!d) return '';
  return d.toLocaleString(undefined, options);
}

/**
 * TREATX — OFFERS UX FINAL CLOSURE (2026-09) — Owner-approved LOCALE-AWARE
 * presentation for offer surfaces. PRESENTATION ONLY; wire/DB stays canonical
 * ISO (YYYY-MM-DD) and all business/expiry semantics are untouched.
 *
 *   Turkish (`tr`)      → DD.MM.YYYY   (e.g. 04.09.2026)
 *   English / global    → MM/DD/YYYY   (e.g. 09/04/2026)
 *
 * Timezone-safe: ISO date strings are split by string components so the day
 * never rebases across timezones.
 */
export function formatDateLocale(value, locale) {
  if (value == null || value === '') return '';
  let yyyy, mm, dd;
  if (typeof value === 'string') {
    const iso = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (iso) { yyyy = iso[1]; mm = iso[2]; dd = iso[3]; }
  }
  if (!yyyy) {
    const d = toDate(value);
    if (!d) return '';
    yyyy = String(d.getFullYear());
    mm = String(d.getMonth() + 1).padStart(2, '0');
    dd = String(d.getDate()).padStart(2, '0');
  }
  const isTr = String(_activeLocale(locale)).toLowerCase().startsWith('tr');
  return isTr ? `${dd}.${mm}.${yyyy}` : `${mm}/${dd}/${yyyy}`;
}

/**
 * Absolute DATE + TIME. The DATE portion follows the Owner locale standard
 * (via formatDateLocale); the TIME portion is preserved as 24h HH:mm in the
 * user's runtime locale. Timezone behavior is unchanged (local wall clock,
 * same as the native Date methods this replaces).
 */
export function formatDateTimeLocale(value, locale, timeOptions) {
  const datePart = formatDateLocale(value, locale);
  if (!datePart) return '';
  const d = toDate(value);
  if (!d) return datePart;
  const timePart = d.toLocaleTimeString(undefined, timeOptions || DEFAULT_TIME_OPTIONS);
  return `${datePart} ${timePart}`;
}

/**
 * BCP-47 lang for a native <input type="date"> so the browser renders the
 * placeholder/format in the app locale (tr → gg.aa.yyyy, en → mm/dd/yyyy)
 * instead of the OS locale. Value stays canonical ISO.
 */
export function dateInputLang(locale) {
  return String(_activeLocale(locale)).toLowerCase().startsWith('tr') ? 'tr' : 'en';
}

/**
 * Locale-aware separator + placeholder for masked manual date entry.
 * TR → '.' / 'gg.aa.yyyy' ; EN/global → '/' / 'mm/dd/yyyy'.
 */
export function dateSeparator(locale) {
  return String(_activeLocale(locale)).toLowerCase().startsWith('tr') ? '.' : '/';
}
export function datePlaceholderText(locale) {
  return String(_activeLocale(locale)).toLowerCase().startsWith('tr') ? 'gg.aa.yyyy' : 'mm/dd/yyyy';
}

/**
 * Parse a locale-formatted manual date entry into canonical ISO YYYY-MM-DD.
 * TR order = DD<sep>MM<sep>YYYY ; EN/global order = MM<sep>DD<sep>YYYY.
 * Accepts '.', '/' or '-' separators leniently. Returns '' when the value is
 * incomplete, not a real calendar date (e.g. 31.02.2026), or outside min/max.
 * This is the presentation↔ISO bridge only; storage stays ISO.
 */
export function parseDateLocale(masked, locale, opts = {}) {
  const { min, max } = opts;
  const m = String(masked || '').trim().match(/^(\d{2})[./-](\d{2})[./-](\d{4})$/);
  if (!m) return '';
  const isTr = String(_activeLocale(locale)).toLowerCase().startsWith('tr');
  const dd = isTr ? m[1] : m[2];
  const mm = isTr ? m[2] : m[1];
  const yyyy = m[3];
  const iso = `${yyyy}-${mm}-${dd}`;
  const d = new Date(iso + 'T00:00:00Z');
  if (Number.isNaN(d.getTime())) return '';
  if (
    d.getUTCFullYear() !== Number(yyyy)
    || d.getUTCMonth() + 1 !== Number(mm)
    || d.getUTCDate() !== Number(dd)
  ) return '';
  if (min && iso < min) return '';
  if (max && iso > max) return '';
  return iso;
}

export default {
  formatDate,
  formatTime,
  formatDateTime,
  formatDateDDMMYYYY,
  formatDateTimeDDMMYYYY,
  parseDateDDMMYYYY,
  formatDateLocale,
  formatDateTimeLocale,
  dateInputLang,
  dateSeparator,
  datePlaceholderText,
  parseDateLocale,
};

/**
 * TREATX-BATCH-1.2 D1 — Owner-approved DD/MM/YYYY presentation.
 * The wire/DB format remains canonical ISO (YYYY-MM-DD).
 *
 * TREATX-BATCH-1.2.1 (2026-02) — timezone-safe rewrite. `new Date("2026-07-03")`
 * parses ISO strings as UTC midnight; `getDate()` then returns local wall-clock,
 * which shifts the rendered day by one in negative-offset zones. We now
 * split ISO strings by string components and never round-trip them through
 * `Date` unless the caller passed a real `Date` object.
 *
 * Accepts an ISO string, a `Date`, or an already-DD/MM/YYYY string
 * and returns a DD/MM/YYYY string. Empty/invalid values return "".
 */
export function formatDateDDMMYYYY(value) {
  if (value == null || value === '') return '';
  // Already DD/MM/YYYY? — normalise separators and zero-pad.
  if (typeof value === 'string') {
    const eu = value.trim().match(/^(\d{1,2})[\/\-\.](\d{1,2})[\/\-\.](\d{4})$/);
    if (eu) {
      const dd = eu[1].padStart(2, '0');
      const mm = eu[2].padStart(2, '0');
      return `${dd}/${mm}/${eu[3]}`;
    }
    // ISO YYYY-MM-DD or YYYY-MM-DDTHH:mm:ssZ — parse by STRING to
    // avoid any timezone rebasing.
    const iso = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;
  }
  // Real Date (or ISO with a time component we couldn't string-match).
  const d = toDate(value);
  if (!d) return '';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${d.getFullYear()}`;
}

/**
 * TREATX-BATCH-1.2 D1 — Parse a DD/MM/YYYY input into canonical ISO
 * (YYYY-MM-DD). Accepts DD/MM/YYYY, DD-MM-YYYY, DD.MM.YYYY, or an
 * already-ISO string (pass-through). Returns "" for invalid input.
 */
export function parseDateDDMMYYYY(value) {
  if (value == null || value === '') return '';
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return '';
    const y = value.getFullYear();
    const m = String(value.getMonth() + 1).padStart(2, '0');
    const d = String(value.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
  const s = String(value).trim();
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
  const eu = s.match(/^(\d{1,2})[\/\-\.](\d{1,2})[\/\-\.](\d{4})$/);
  if (eu) {
    const dd = eu[1].padStart(2, '0');
    const mm = eu[2].padStart(2, '0');
    return `${eu[3]}-${mm}-${dd}`;
  }
  return '';
}

/**
 * TREATX-B-2.1 (2026-02) — Canonical DD/MM/YYYY HH:mm:ss (24-hour)
 * presentation for user-facing timestamps that need BOTH date AND
 * time. Owner mandate: no `toLocaleString()` / `toLocaleDateString()`
 * with `en-US` locale, no M/D/YYYY drift, no 12-hour AM/PM. Wire /
 * storage remains canonical ISO / epoch.
 *
 * Accepts an ISO string or a `Date`. Empty/invalid → "".
 * The date portion reuses `formatDateDDMMYYYY` so day/month order
 * cannot drift out of sync with the rest of the app.
 */
export function formatDateTimeDDMMYYYY(value) {
  if (value == null || value === '') return '';
  const datePart = formatDateDDMMYYYY(value);
  if (!datePart) return '';
  const d = toDate(value);
  if (!d) return datePart;
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  const ss = String(d.getSeconds()).padStart(2, '0');
  return `${datePart} ${hh}:${mm}:${ss}`;
}
